import Header from '../components/Header';
import Footer from '../components/Footer';
import { Apple, Zap, Briefcase, Rocket, Smartphone, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Services() {
  const serviceItems = [
    {
      id: 'quick-loan',
      title: 'Quick Loan',
      subtitle: 'Instant capital in minutes',
      desc: 'Our proprietary evaluation protocol provides immediate liquidity for individual or business needs. No manual paperwork required.',
      features: ['Instant processing', 'Low interest rates', 'Flexible repayment', 'No collateral'],
      icon: Zap,
      color: 'text-yellow-500',
      bg: 'bg-yellow-50',
      badge: 'POPULAR'
    },
    {
      id: 'business-loan',
      title: 'Business Loans',
      subtitle: 'Fuel your enterprise growth',
      desc: 'Optimized credit for growing SMEs. We offer customized interest strategies suited to your cash flow requirements.',
      features: ['High credit limit', 'Repayment holidays', 'Business advisor included', 'Multi-disbursement'],
      icon: Briefcase,
      color: 'text-primary-500',
      bg: 'bg-primary-50',
      badge: 'STRATEGIC'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Page Header */}
      <section className="pt-16 pb-16 bg-stripe-bg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-center space-y-6">
           <h1 className="text-4xl md:text-6xl font-black text-stripe-dark tracking-tighter">Our Services</h1>
           <p className="text-xl text-stripe-slate max-w-2xl mx-auto leading-relaxed">Financial products architected for speed, transparency, and scalability.</p>
        </div>
        <div className="absolute inset-0 bg-white/40 -z-10" />
      </section>

      {/* Services Grid */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-24">
           {serviceItems.map((s, i) => (
             <div key={i} id={s.id} className="s-card p-12 lg:p-16 border-2 border-transparent hover:border-primary-500/20 transition-all group overflow-hidden relative">
                <div className="relative z-10 space-y-12">
                   <div className="flex items-center justify-between">
                      <div className={`w-16 h-16 ${s.bg} rounded-3xl flex items-center justify-center ${s.color} shadow-sm group-hover:scale-110 transition-transform`}>
                         <s.icon size={28} />
                      </div>
                      <span className="text-[10px] font-black px-4 py-1.5 bg-primary-50 text-primary-600 rounded-full border border-primary-100 tracking-[0.2em]">{s.badge}</span>
                   </div>
                   
                   <div>
                      <h2 className="text-3xl font-black text-stripe-dark tracking-tight mb-3">{s.title}</h2>
                      <p className="text-primary-500 font-bold uppercase tracking-widest text-xs mb-6">{s.subtitle}</p>
                      <p className="text-lg text-stripe-slate leading-relaxed">{s.desc}</p>
                   </div>
                   
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                      {s.features.map((f, j) => (
                        <div key={j} className="flex items-center gap-3">
                           <CheckCircle2 size={16} className="text-primary-500" />
                           <span className="text-sm font-bold text-stripe-dark">{f}</span>
                        </div>
                      ))}
                   </div>
                   
                   <div className="pt-10">
                      <Link to="/register" className="btn-primary w-full py-4 rounded-full text-base font-black shadow-lg">
                        Apply Now
                      </Link>
                   </div>
                </div>
                
                {/* Visual Flair */}
                <div className="absolute top-0 right-0 p-12 text-stripe-bg -z-10 group-hover:text-primary-50/50 transition-colors">
                  <s.icon size={180} />
                </div>
             </div>
           ))}
        </div>
      </section>

      {/* FAQ Hook */}
      <section className="py-24 bg-stripe-bg/50">
         <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-12">
            <h3 className="text-2xl font-black text-stripe-dark tracking-tight italic">Simplified Architecture</h3>
            <div className="grid sm:grid-cols-3 gap-12">
               {[
                 { label: 'Application', desc: 'Secure digital form with instant automated KYC.', icon: Smartphone },
                 { label: 'Evaluation', desc: 'Real-time protocol calculates credit tier.', icon: ShieldCheck },
                 { label: 'Disbursement', desc: 'Capital sent directly to your linked bank account.', icon: Rocket }
               ].map((f, i) => (
                 <div key={i} className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-primary-500 border border-stripe-border">
                       <f.icon size={20}/>
                    </div>
                    <div>
                       <p className="text-sm font-bold text-stripe-dark mb-1">{f.label}</p>
                       <p className="text-xs text-stripe-slate font-medium leading-relaxed">{f.desc}</p>
                    </div>
                 </div>
               ))}
            </div>
         </div>
      </section>

      <Footer />
    </div>
  );
}
